"use client";

import { useState } from "react";
import Link from "next/link";
import type { CSSProperties } from "react";
import sx from "../_data/structure.json";

type Area = {
  key: string;
  label: string;
  sub: string;
  n: number;
  c: string;
  cl: string;
  cd: string;
  live: boolean;
  blocks: string[];
  dest: string;
};

type Item = {
  name: string;
  sig?: string;
  strengths?: string[];
  qty?: string[];
  price?: string;
  cs?: string | null;
  hold?: string | null;
};

type FormBlock = { sub: string; hot?: boolean; items: Item[] };
type ListedRow = { name: string; size: string; price: string; cs?: boolean };
type Preset = {
  i: number;
  name: string;
  purpose: string;
  form: string;
  amount: string;
  days: number;
  refills: number;
  cs: boolean;
};
type StrRow = {
  label: string;
  sub: string | null;
  blocked?: boolean;
  hk: string;
  cs?: boolean;
  strengths: string[];
  unit: string;
  ratios: string[];
};

const D = sx as unknown as {
  areas: Area[];
  bhrt: {
    presetName: string;
    forms: string[];
    presets: Preset[];
    blocks: Record<string, { key: string; sub: string; rows: StrRow[] }>;
  };
  listed: Record<string, Record<string, ListedRow[]>>;
  form: Record<string, Record<string, FormBlock>>;
  build: Record<
    string,
    {
      lede: string;
      sig: string;
      qty: string[];
      groups: [string, [string, string[], string | null][]][];
    }
  >;
  buildName: string;
  priceNote: Record<string, string>;
  ladder: { n: string; p: string }[];
};

const writeHref = (o: Record<string, string | undefined>) => {
  const q = Object.entries(o)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `/provider/write?${q}`;
};

const areaVars = (a: Area) =>
  ({ "--ac": a.c, "--acl": a.cl, "--acd": a.cd }) as CSSProperties;

export default function StructureBoard() {
  const [areaKey, setAreaKey] = useState(D.areas[0].key);
  const [block, setBlock] = useState(D.areas[0].blocks[0]);
  const [pick, setPick] = useState<Record<string, string>>({});

  const area = D.areas.find((a) => a.key === areaKey)!;

  const chooseArea = (a: Area) => {
    setAreaKey(a.key);
    setBlock(a.blocks[0]);
    setPick({});
  };

  const chooseBlock = (name: string) => {
    setBlock(name);
    setPick({});
  };

  const toggle = (key: string, v: string) =>
    setPick((p) => {
      const n = { ...p };
      if (n[key] === v) delete n[key];
      else n[key] = v;
      return n;
    });

  return (
    <div className="sxwrap">
      <div className="sxlegend">
        <span>
          <b>Colour code</b>
        </span>
        {D.areas.map((a) => (
          <span key={a.key}>
            <i style={{ background: a.c }} />
            {a.label}
          </span>
        ))}
      </div>

      <div className="sxribwrap">
        <div className="sxrib">
          {D.areas.map((a) => (
            <button
              key={a.key}
              type="button"
              className={"sxribb" + (a.key === areaKey ? " is-on" : "")}
              style={areaVars(a)}
              onClick={() => chooseArea(a)}
            >
              <b>{a.label}</b>
              <em>
                {a.n} preparations &middot; {a.sub}
              </em>
            </button>
          ))}
        </div>

        <div className="sxroom" style={areaVars(area)}>
          <div className="sxroom__h">
            <h2>{area.label}</h2>
            <em>
              {area.n} preparations &middot; {area.sub}
            </em>
            <span className="sxsp" />
          </div>

          <div className="sxblocks" role="tablist">
            {area.blocks.map((b, bi) => {
              const hot = (D as any).hot?.[area.key] || [];
              const brk =
                area.blocks.length > 6 ? Math.ceil(area.blocks.length / 2) : -1;
              return (
                <FragmentBlock key={b} breakBefore={bi === brk}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={b === block}
                    className={
                      "sxblock" +
                      (hot.includes(b) ? " sxblock--hot" : "") +
                      (b === block ? " is-on" : "")
                    }
                    onClick={() => chooseBlock(b)}
                  >
                    {b}
                  </button>
                </FragmentBlock>
              );
            })}
          </div>

          <div className="sxbody">
            {area.live ? (
              <LiveBlock block={block} bhrt={D.bhrt} />
            ) : D.form[area.key] ? (
              <FormBlockView
                area={area}
                block={block}
                pick={pick}
                toggle={toggle}
                form={D.form[area.key]}
                build={D.build[area.key]}
                buildName={D.buildName}
                priceNote={D.priceNote[area.key]}
                ladder={D.ladder}
              />
            ) : (
              <ListedBlock
                area={area}
                block={block}
                listed={D.listed}
                ladder={D.ladder}
              />
            )}
          </div>
        </div>
      </div>

      <p className="sxfine">
        Nothing here is chosen for you. Pick a group, tick a strength and a
        quantity, and the finished line appears with one way onward &mdash;
        written out, ready to sign.
      </p>
    </div>
  );
}

function FragmentBlock({
  breakBefore,
  children,
}: {
  breakBefore: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      {breakBefore && <span className="sxbrk" />}
      {children}
    </>
  );
}

function LiveBlock({
  block,
  bhrt,
}: {
  block: string;
  bhrt: typeof D extends { bhrt: infer B } ? B : never;
}) {
  if (block === bhrt.presetName) {
    return (
      <div>
        <p className="sxlede">
          Approved by the pharmacy and filled in already. Click one and it opens
          ready to sign &mdash; change anything you like before you do.
        </p>
        <ul className="sxpre">
          {bhrt.presets.map((x) => (
            <li key={x.i}>
              <Link className="sxpre__a" href="/provider/bhrt">
                <b>{x.name}</b>
                <em>
                  {x.purpose} &middot; {x.form}
                </em>
                <span className="sxpre__m">
                  {x.amount} daily &middot; {x.days} days &middot; {x.refills}{" "}
                  refills
                </span>
                {x.cs && <span className="sxciii">C-III</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const b = bhrt.blocks[block];
  if (!b) return <p className="sxlede">{block}</p>;

  return (
    <div>
      <p className="sxlede">
        Click a strength and it opens with that line already started. Every
        strength below can be made in any of the six dose forms.
      </p>
      <div className="sxstr">
        {b.rows.map((r) => (
          <div className="sxstr__r" key={r.label}>
            <div className="sxstr__n">
              <b>{r.label}</b>
              {r.sub && <em>{r.sub}</em>}
            </div>
            <div className="sxstr__v">
              {r.blocked ? (
                <span className="sxoff">Awaiting the pharmacist-in-charge</span>
              ) : (
                <>
                  {r.strengths.map((v) => (
                    <Link className="sxpill" href="/provider/bhrt" key={v}>
                      {v} {r.unit}
                    </Link>
                  ))}
                  {r.ratios.map((rt) => (
                    <Link
                      className="sxpill sxpill--q"
                      href="/provider/bhrt"
                      key={rt}
                    >
                      {rt === "split" ? "each in mg" : rt}
                    </Link>
                  ))}
                  {r.cs && <span className="sxciii">C-III</span>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="sxforms">
        <b>Six dose forms, any strength:</b> {bhrt.forms.join(" · ")}. Strengths
        are milligrams per gram in the creams and the gel, and milligrams per
        unit in the capsules and the lozenge.
      </p>
    </div>
  );
}

function ItemRow({
  x,
  isHot,
  pk,
  pick,
  toggle,
}: {
  x: Item;
  isHot: boolean;
  pk: string;
  pick: Record<string, string>;
  toggle: (k: string, v: string) => void;
}) {
  const needS = !!(x.strengths && x.strengths.length);
  const needQ = !!(x.qty && x.qty.length);
  const s = needS ? pick[pk + "|s"] : undefined;
  const q = needQ ? pick[pk + "|q"] : undefined;

  let go: React.ReactNode = null;
  if (!x.hold && (!needS || s) && (!needQ || q)) {
    const line = x.name + (s ? " · " + s : "") + (q ? " · " + q : "");
    const pr = priceFor(x.price, q);
    go = (
      <div className="sxgo">
        <div className="sxgo__t">
          <b>{line}</b>
          <span>
            {x.sig}
            {pr ? " · " + pr : ""}
          </span>
        </div>
        <Link
          className="sxgo__b"
          href={writeHref({
            n: x.name,
            s,
            q,
            d: x.sig,
            p: pr,
            cs: x.cs ?? undefined,
          })}
        >
          Write it and sign &rarr;
        </Link>
      </div>
    );
  } else if (!x.hold && (s || q)) {
    const need: string[] = [];
    if (needS && !s) need.push("a strength");
    if (needQ && !q) need.push("a quantity");
    go = (
      <div className="sxgo sxgo--need">
        <div className="sxneed">Choose {need.join(" and ")}.</div>
      </div>
    );
  }

  return (
    <div className={"sxit" + (isHot ? " sxit--hot" : "")}>
      {isHot && <span className="sxit--hot__t">Most prescribed</span>}
      <div className="sxit__h">
        <b>
          {x.name}
          {x.cs ? ` ${x.cs}` : ""}
        </b>
        {x.price && <span className="sxit__p">{x.price}</span>}
      </div>
      {x.sig && <em className="sxit__s">{x.sig}</em>}
      {needS && (
        <Pills
          label="Strength"
          vals={x.strengths!}
          k={pk + "|s"}
          pick={pick}
          toggle={toggle}
        />
      )}
      {needQ && (
        <Pills
          label="Quantity"
          vals={x.qty!}
          dashed
          k={pk + "|q"}
          pick={pick}
          toggle={toggle}
        />
      )}
      {go}
      {x.hold && (
        <div className="sxwait">
          <b>Held.</b> {x.hold}
        </div>
      )}
    </div>
  );
}

function priceFor(price: string | undefined, q: string | undefined) {
  if (!price) return "";
  const parts = price.split(" · ");
  if (parts.length > 1 && q) {
    const n = (q.match(/[0-9.]+/) || [null])[0];
    for (const p of parts) {
      const pn = (p.match(/[0-9.]+/) || [null])[0];
      if (n && pn === n) {
        const d = p.match(/\$[0-9.,]+/);
        return d ? d[0] : p;
      }
    }
  }
  return price;
}

function Pills({
  label,
  vals,
  k,
  pick,
  toggle,
  dashed,
}: {
  label: string;
  vals: string[];
  k: string;
  pick: Record<string, string>;
  toggle: (key: string, v: string) => void;
  dashed?: boolean;
}) {
  return (
    <div className="sxit__r">
      <span className="sxit__k">{label}</span>
      {vals.map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={pick[k] === v}
          className={
            "sxpill" +
            (dashed ? " sxpill--q" : "") +
            (pick[k] === v ? " is-on" : "")
          }
          onClick={() => toggle(k, v)}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function FormBlockView({
  area,
  block,
  pick,
  toggle,
  form,
  build,
  buildName,
  priceNote,
  ladder,
}: {
  area: Area;
  block: string;
  pick: Record<string, string>;
  toggle: (k: string, v: string) => void;
  form: Record<string, FormBlock>;
  build?: {
    lede: string;
    sig: string;
    qty: string[];
    groups: [string, [string, string[], string | null][]][];
  };
  buildName: string;
  priceNote: string;
  ladder: { n: string; p: string }[];
}) {
  if (block === buildName && build) {
    return (
      <BuildBlock
        area={area}
        build={build}
        pick={pick}
        toggle={toggle}
        ladder={ladder}
      />
    );
  }
  const b = form[block];
  if (!b) return <p className="sxlede">{block}</p>;
  return (
    <div>
      <p className="sxlede">
        {b.sub}, exactly as the pharmacy&rsquo;s own order form carries them.{" "}
        {priceNote}
      </p>
      {b.items.map((x, i) => (
        <ItemRow
          key={x.name}
          x={x}
          isHot={!!b.hot}
          pk={`${area.key}|${block}|${i}`}
          pick={pick}
          toggle={toggle}
        />
      ))}
      <p className="sxforms">
        <b>
          {area.n} preparations in {area.label},
        </b>{" "}
        in the groups the form itself uses. No builder in this area yet: pick a
        line and the order is written from it.
      </p>
    </div>
  );
}

function BuildBlock({
  area,
  build,
  pick,
  toggle,
  ladder,
}: {
  area: Area;
  build: {
    lede: string;
    sig: string;
    qty: string[];
    groups: [string, [string, string[], string | null][]][];
  };
  pick: Record<string, string>;
  toggle: (k: string, v: string) => void;
  ladder: { n: string; p: string }[];
}) {
  const picked: string[] = [];
  build.groups.forEach(([g, ings]) => {
    ings.forEach(([name, vals]) => {
      const v = pick[`${area.key}|build|${g}|${name}`];
      if (v) picked.push(`${name} ${v}`);
    });
  });
  const q = pick[`${area.key}|build|qty`];

  let go: React.ReactNode = null;
  if (picked.length && q) {
    const bi =
      picked.length <= 3 ? 0 : Math.min(picked.length - 3, ladder.length - 1);
    const band = ladder[bi];
    go = (
      <div className="sxgo">
        <div className="sxgo__t">
          <b>{picked.join(" + ")}</b>
          <span>
            {q} &middot; {build.sig} &middot; {picked.length}
            {picked.length === 1 ? " active" : " actives"} &middot; {band.p}
          </span>
        </div>
        <Link
          className="sxgo__b"
          href={writeHref({
            n: picked.join(" + "),
            q,
            d: build.sig,
            p: band.p,
            a: area.key,
          })}
        >
          Write it and sign &rarr;
        </Link>
      </div>
    );
  } else if (picked.length && !q) {
    go = (
      <div className="sxgo sxgo--need">
        <div className="sxneed">Choose a quantity.</div>
      </div>
    );
  }

  return (
    <div>
      <p className="sxlede">{build.lede}</p>
      {build.groups.map(([g, ings]) => (
        <div className="sxgrp" key={g}>
          <div className="sxgrp__h">{g}</div>
          {ings.map(([name, vals, cs]) => (
            <div className="sxgrp__r" key={name}>
              <span className="sxgrp__n">{name}</span>
              {vals.map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={pick[`${area.key}|build|${g}|${name}`] === v}
                  className={
                    "sxpill" +
                    (pick[`${area.key}|build|${g}|${name}`] === v
                      ? " is-on"
                      : "")
                  }
                  onClick={() => toggle(`${area.key}|build|${g}|${name}`, v)}
                >
                  {v}
                </button>
              ))}
              {cs && <span className="sxciii">{cs}</span>}
            </div>
          ))}
        </div>
      ))}
      <p className="sxforms">
        <b>Directions:</b> {build.sig}.
      </p>
      <Pills
        label="Quantity"
        vals={build.qty}
        dashed
        k={`${area.key}|build|qty`}
        pick={pick}
        toggle={toggle}
      />
      {go}
      <div className="sxlad">
        {ladder.map((x) => (
          <div className="sxlad__r" key={x.n}>
            <b>{x.n}</b>
            <span>{x.p}</span>
          </div>
        ))}
      </div>
      <div className="sxwait">
        <b>Asked for, not built yet.</b> BHRT is the only area with a working
        build-your-own screen. Until this one is built, the actives you pick are
        written on the order and the pharmacy confirms it before it is made.
      </div>
    </div>
  );
}

function ListedBlock({
  area,
  block,
  listed,
  ladder,
}: {
  area: Area;
  block: string;
  listed: typeof D.listed;
  ladder: { n: string; p: string }[];
}) {
  const rows = (listed[area.key] || {})[block] || [];

  if (block === "Combination") {
    return (
      <div>
        <p className="sxlede">
          Name the actives you want and the pharmacy compounds them in one
          preparation. What the patient pays follows the number of actives, and
          nothing else.
        </p>
        <div className="sxlad">
          {ladder.map((x) => (
            <div className="sxlad__r" key={x.n}>
              <b>{x.n}</b>
              <span>{x.p}</span>
            </div>
          ))}
        </div>
        <div className="sxwait">
          <b>Asked for, not built yet.</b> BHRT is the only area with a
          build-your-own screen. Until this one is built, a combination in this
          area is written on the order and the pharmacy confirms it before it is
          made.
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="sxwait">
        <b>Nothing listed in this block yet.</b>
      </div>
    );
  }

  return (
    <div>
      <p className="sxlede">
        Already approved and priced. Click one and it opens written out, with
        the strength, the size and what your patient pays all filled in.
      </p>
      <ul className="sxpre">
        {rows.map((x) => (
          <li key={x.name}>
            <Link
              className="sxpre__a"
              href={writeHref({
                n: x.name,
                s: x.size,
                p: x.price,
                cs: x.cs ? "C-III" : undefined,
                a: area.key,
              })}
            >
              <b>{x.name}</b>
              <em>{x.size}</em>
              <span className="sxpre__m">{x.price}</span>
              {x.cs && <span className="sxciii">C-III</span>}
            </Link>
          </li>
        ))}
      </ul>
      <p className="sxforms">
        <b>
          {area.n} preparations in {area.label}.
        </b>{" "}
        The whole list, with every strength and size, is in the formulary. No
        builder in this area yet.
      </p>
    </div>
  );
}
